import { z } from 'zod';
/**
 * Codes d'erreur stables de l'API (docs/api/API_GUIDELINES.md §5).
 * Le message est localisé côté serveur ; le code ne change jamais.
 */
export const ErrorCode = z.enum([
    // 400
    'VALIDATION_ERROR',
    // 401
    'UNAUTHENTICATED',
    'SESSION_EXPIRED',
    'MFA_REQUIRED',
    'INVALID_CREDENTIALS',
    'ACCOUNT_LOCKED',
    'ACCOUNT_DISABLED',
    'CSRF_TOKEN_INVALID',
    // 403
    'PERMISSION_DENIED',
    /** Le tenant est suspendu par la plateforme : connexion et session refusées (ADR-021, R-PLT-06). */
    'TENANT_SUSPENDED',
    /** Console plateforme : le second facteur doit être activé sur le compte (ADR-021, A-069). */
    'PLATFORM_MFA_REQUIRED',
    /** Session en mode support : toute écriture est refusée (ADR-022, R-SUP-02). */
    'SUPPORT_READ_ONLY',
    // 404
    'NOT_FOUND',
    /** Route d'un module non activé pour le tenant : la fonctionnalité n'existe pas pour lui (ADR-014). */
    'MODULE_NOT_ENABLED',
    // 409
    'CONFLICT',
    'OPTIMISTIC_LOCK_CONFLICT',
    'EMAIL_ALREADY_USED',
    'ROLE_IN_USE',
    'SYSTEM_ROLE_IMMUTABLE',
    'RESERVATION_INVALID_TRANSITION',
    'RESOURCE_NOT_AVAILABLE',
    'INVOICE_COMPLIANCE_DOCUMENTS_MISSING',
    'INVOICE_IMMUTABLE',
    'INVOICE_ALREADY_EXISTS',
    'BILLING_DOCUMENT_NOT_EDITABLE',
    'PROFORMA_NOT_ISSUED',
    'CREDIT_NOTE_EXCEEDS_INVOICE',
    'PAYMENT_EXCEEDS_BALANCE',
    'PAYMENT_ALREADY_REVERSED',
    'PAYMENT_ALLOCATION_EXCEEDS_AMOUNT',
    'REFUND_EXCEEDS_PAYMENT',
    'CASH_SESSION_ALREADY_OPEN',
    'CASH_SESSION_NOT_OPEN',
    'CASH_SESSION_CLOSED',
    /** Activation impossible : module indisponible, hors abonnement ou dépendance manquante (ADR-014). */
    'MODULE_UNAVAILABLE',
    /** Check-out refusé : solde restant dû sans motif ni permission (R-010). */
    'BALANCE_DUE',
    /** Commande POS : opération refusée par son état (payée, annulée, ligne déjà envoyée…) (ADR-016). */
    'ORDER_INVALID_TRANSITION',
    /** Commande POS : des paiements sont enregistrés — annuler ou rembourser avant d'annuler la commande. */
    'ORDER_HAS_PAYMENTS',
    /** Table déjà occupée par une commande ouverte. */
    'TABLE_OCCUPIED',
    /** Encaissement POS : moyen de paiement non accepté par le point de vente (ADR-026, D-H78). */
    'PAYMENT_METHOD_NOT_ACCEPTED',
    /** Produit inactif, en rupture ou hors de la carte du point de vente. */
    'PRODUCT_UNAVAILABLE',
    /** Bar : une note ouverte porte déjà ce nom au point de vente (ADR-019, R-BAR-02). */
    'TAB_NAME_IN_USE',
    /** Folio : le séjour n'est pas en cours (`CHECKED_IN`) — aucune charge possible (R-FOL-01). */
    'STAY_NOT_IN_HOUSE',
    /** Folio : le séjour n'appartient pas à l'établissement du point de vente (R-FOL-04). */
    'STAY_PROPERTY_MISMATCH',
    /** Folio clos par le check-out : plus aucune charge (R-FOL-01). */
    'FOLIO_CLOSED',
    /** Charge déjà annulée. */
    'FOLIO_CHARGE_VOIDED',
    /** Stock : code d'article ou de lieu déjà utilisé dans l'établissement (ADR-020). */
    'STOCK_CODE_IN_USE',
    /** Stock : article, lieu ou point de vente d'un autre établissement, ou lieu inactif. */
    'STOCK_LOCATION_MISMATCH',
    /** Stock : le bon d'achat n'est plus un brouillon (reçu ou annulé). */
    'STOCK_PURCHASE_NOT_DRAFT',
    /** Stock : un comptage est déjà ouvert sur ce lieu (R-INV-06). */
    'STOCK_COUNT_ALREADY_OPEN',
    /** Stock : le comptage n'est plus ouvert. */
    'STOCK_COUNT_NOT_OPEN',
    /** Plateforme : identifiant de tenant (`slug`) déjà pris (R-PLT-05). */
    'TENANT_SLUG_IN_USE',
    /** Plateforme : transition de statut impossible (déjà suspendu, déjà actif…) (R-PLT-06). */
    'TENANT_INVALID_TRANSITION',
    /** Accès support : la demande n'est pas approuvée, a expiré, est révoquée ou terminée (ADR-022). */
    'SUPPORT_ACCESS_NOT_ACTIVE',
    /** Accès support : une demande en attente ou approuvée existe déjà pour cet opérateur et ce tenant (R-SUP-01). */
    'SUPPORT_ACCESS_ALREADY_OPEN',
    /** Accès support : décision impossible dans l'état actuel de la demande. */
    'SUPPORT_ACCESS_INVALID_TRANSITION',
    /** Abonnement : limite du plan atteinte (établissements, ressources, utilisateurs) (ADR-023, R-PAY-05). */
    'PLAN_LIMIT_REACHED',
    /** Abonnement facturé sans périodicité, prix ou échéance d'essai (ADR-023). */
    'SUBSCRIPTION_BILLING_INCOMPLETE',
    /** Facture d'abonnement : transition impossible (déjà réglée, annulée…). */
    'SUBSCRIPTION_INVOICE_INVALID_TRANSITION',
    /** Facture d'abonnement : le règlement dépasse le solde restant. */
    'SUBSCRIPTION_PAYMENT_EXCEEDS_BALANCE',
    /** Sollicitation commerciale sans consentement accordé pour le canal (ADR-025, R-CRM-05). */
    'CONSENT_REQUIRED',
    /** Programme de fidélité absent ou inactif (ADR-025, R-CRM-03). */
    'LOYALTY_PROGRAM_INACTIVE',
    /** Utilisation de points supérieure au solde (ADR-025, R-CRM-04). */
    'LOYALTY_INSUFFICIENT_POINTS',
    /** Fusion refusée : même fiche, natures différentes ou fiche déjà fusionnée (ADR-025, R-CRM-06). */
    'CUSTOMER_MERGE_INVALID',
    /** La commande porte déjà une remise : une seule remise par commande (ADR-025, D-H64). */
    'ORDER_ALREADY_DISCOUNTED',
    // 413 / 415
    'UPLOAD_TOO_LARGE',
    'UPLOAD_UNSUPPORTED_TYPE',
    // 422
    'PASSWORD_POLICY_VIOLATION',
    'INVITATION_INVALID',
    'TOKEN_INVALID',
    'PERMISSION_ESCALATION',
    /** Confirmation refusée : aucune avance enregistrée (R-013, D-020/D-022). */
    'DEPOSIT_REQUIRED',
    /**
     * Même `Idempotency-Key` rejouée avec un corps différent
     * (SECURITY_ARCHITECTURE §5.6) : la première réponse reste seule valable.
     */
    'IDEMPOTENCY_KEY_REUSED',
    // 429
    'RATE_LIMITED',
    // 500
    'INTERNAL_ERROR',
    // 501
    'NOT_IMPLEMENTED',
]);
export const ApiError = z.object({
    error: z.object({
        code: ErrorCode,
        message: z.string(),
        details: z.record(z.unknown()).optional(),
        traceId: z.string().optional(),
    }),
});
export const HTTP_STATUS_BY_CODE = {
    VALIDATION_ERROR: 400,
    UNAUTHENTICATED: 401,
    SESSION_EXPIRED: 401,
    MFA_REQUIRED: 401,
    INVALID_CREDENTIALS: 401,
    ACCOUNT_LOCKED: 401,
    ACCOUNT_DISABLED: 401,
    CSRF_TOKEN_INVALID: 403,
    PERMISSION_DENIED: 403,
    TENANT_SUSPENDED: 403,
    PLATFORM_MFA_REQUIRED: 403,
    SUPPORT_READ_ONLY: 403,
    NOT_FOUND: 404,
    MODULE_NOT_ENABLED: 404,
    CONFLICT: 409,
    OPTIMISTIC_LOCK_CONFLICT: 409,
    EMAIL_ALREADY_USED: 409,
    ROLE_IN_USE: 409,
    SYSTEM_ROLE_IMMUTABLE: 409,
    RESERVATION_INVALID_TRANSITION: 409,
    RESOURCE_NOT_AVAILABLE: 409,
    INVOICE_COMPLIANCE_DOCUMENTS_MISSING: 409,
    INVOICE_IMMUTABLE: 409,
    INVOICE_ALREADY_EXISTS: 409,
    BILLING_DOCUMENT_NOT_EDITABLE: 409,
    PROFORMA_NOT_ISSUED: 409,
    CREDIT_NOTE_EXCEEDS_INVOICE: 422,
    PAYMENT_EXCEEDS_BALANCE: 409,
    PAYMENT_ALREADY_REVERSED: 409,
    PAYMENT_ALLOCATION_EXCEEDS_AMOUNT: 422,
    REFUND_EXCEEDS_PAYMENT: 422,
    CASH_SESSION_ALREADY_OPEN: 409,
    CASH_SESSION_NOT_OPEN: 409,
    CASH_SESSION_CLOSED: 409,
    MODULE_UNAVAILABLE: 409,
    BALANCE_DUE: 409,
    ORDER_INVALID_TRANSITION: 409,
    ORDER_HAS_PAYMENTS: 409,
    TABLE_OCCUPIED: 409,
    PAYMENT_METHOD_NOT_ACCEPTED: 409,
    PRODUCT_UNAVAILABLE: 409,
    TAB_NAME_IN_USE: 409,
    STAY_NOT_IN_HOUSE: 409,
    STAY_PROPERTY_MISMATCH: 409,
    FOLIO_CLOSED: 409,
    FOLIO_CHARGE_VOIDED: 409,
    STOCK_CODE_IN_USE: 409,
    STOCK_LOCATION_MISMATCH: 409,
    STOCK_PURCHASE_NOT_DRAFT: 409,
    STOCK_COUNT_ALREADY_OPEN: 409,
    STOCK_COUNT_NOT_OPEN: 409,
    TENANT_SLUG_IN_USE: 409,
    TENANT_INVALID_TRANSITION: 409,
    PLAN_LIMIT_REACHED: 409,
    SUBSCRIPTION_BILLING_INCOMPLETE: 409,
    SUBSCRIPTION_INVOICE_INVALID_TRANSITION: 409,
    SUBSCRIPTION_PAYMENT_EXCEEDS_BALANCE: 409,
    CONSENT_REQUIRED: 409,
    LOYALTY_PROGRAM_INACTIVE: 409,
    LOYALTY_INSUFFICIENT_POINTS: 409,
    CUSTOMER_MERGE_INVALID: 409,
    ORDER_ALREADY_DISCOUNTED: 409,
    SUPPORT_ACCESS_NOT_ACTIVE: 409,
    SUPPORT_ACCESS_ALREADY_OPEN: 409,
    SUPPORT_ACCESS_INVALID_TRANSITION: 409,
    UPLOAD_TOO_LARGE: 413,
    UPLOAD_UNSUPPORTED_TYPE: 415,
    PASSWORD_POLICY_VIOLATION: 422,
    INVITATION_INVALID: 422,
    TOKEN_INVALID: 422,
    PERMISSION_ESCALATION: 422,
    DEPOSIT_REQUIRED: 422,
    IDEMPOTENCY_KEY_REUSED: 422,
    RATE_LIMITED: 429,
    INTERNAL_ERROR: 500,
    NOT_IMPLEMENTED: 501,
};
//# sourceMappingURL=errors.js.map